import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentChannel } from '../config';
import useCustomFetchApi from '../utils/customFetchApi';
import ShareModal from './ShareModal';
import './PortraitDetailsModal.scss'; // Use the same CSS as PortraitDetailsModal

function ProfilePreviewModal({ profile, onClose }) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [freshProfile, setFreshProfile] = useState(profile); // State for fresh profile data
  const [isLoadingProfile, setIsLoadingProfile] = useState(false); // Loading state for API call
  const [showShare, setShowShare] = useState(false); // Share modal state
  
  const api = useCustomFetchApi();

  // Function to fetch fresh profile data
  const fetchFreshProfile = async () => {
    if (!profile?.uuid) return;
    
    setIsLoadingProfile(true);
    try {
      const response = await api.get(`/user/${profile.uuid}`);
      if (response) {
        setFreshProfile(response);
      }
    } catch (error) {
      console.error('Error fetching fresh profile data:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Fetch fresh data when modal opens
  useEffect(() => {
    if (profile?.uuid) {
      fetchFreshProfile();
    }
  }, [profile?.uuid]);

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const toggleImageZoom = () => setIsImageZoomed(!isImageZoomed);
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setIsFullscreen(!isFullscreen);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Generate profile share URL
  const generateProfileUrl = () => {
    if (!freshProfile?.uuid) return '';
    const currentLang = i18n.language || 'en';
    const currentChannel = getCurrentChannel();
    const baseUrl = window.location.origin;
    return `${baseUrl}/${currentLang}/${currentChannel}/profile/${freshProfile.uuid}`;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isFullscreen]);

  // Reset zoom and fullscreen when profile changes
  useEffect(() => {
    setIsImageZoomed(false);
    setIsFullscreen(false);
    setImageLoaded(false);
    setImageError(false);
    setFreshProfile(profile); // Reset to initial profile when prop changes
  }, [profile]);

  // Control body scrolling when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Use fresh profile data or fallback to original
  const currentProfile = freshProfile || profile;

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get image URL - use image_path directly
  const getImageUrl = () => {
    if (currentProfile?.images && currentProfile.images.length > 0) {
      return currentProfile.images[0].image_path;
    }
    return null;
  };

  return (
    <>
      <div className="overlay" id="portrait-popup" onClick={handleOutsideClick}>
        <div className={`modal ${isImageZoomed ? 'image-zoomed' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <button className="share-button" onClick={() => setShowShare(true)} title={t('dashboard.share', 'Share Profile')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                </svg>
              </button>
              <button className="close-button" onClick={onClose}>×</button>
            </div>
            
            {/* Enhanced image container with zoom functionality */}
            <div className="image-container">
              {/* API Loading state */}
              {isLoadingProfile && (
                <div className="image-loading">
                  <div className="loading-spinner"></div>
                  <p>Fetching latest data...</p>
                </div>
              )}
              
              {/* Image Loading state */}
              {!imageLoaded && !imageError && !isLoadingProfile && getImageUrl() && (
                <div className="image-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading portrait...</p>
                </div>
              )}
              
              {/* Error state */}
              {imageError && (
                <div className="image-error">
                  <div className="error-icon">⚠</div>
                  <p>Failed to load image</p>
                  <button 
                    className="retry-button"
                    onClick={() => {
                      setImageError(false);
                      setImageLoaded(false);
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
              
              {getImageUrl() ? (
                <>
                  <img 
                    src={getImageUrl()}
                    alt={currentProfile.nickname || 'Profile'}
                    className={`${isImageZoomed ? 'zoomed' : ''} ${imageLoaded ? 'loaded' : ''}`}
                    onClick={toggleFullscreen}
                    title="Click to view fullscreen"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: imageError ? 'none' : 'block' }}
                  />
                  
                  {/* Image controls overlay - only show when image is loaded */}
                  {imageLoaded && (
                    <div className="image-controls">
                      <button 
                        className="zoom-button"
                        onClick={toggleImageZoom}
                        title={isImageZoomed ? "Zoom Out" : "Zoom In"}
                      >
                        {isImageZoomed ? '−' : '+'}
                      </button>
                      <button 
                        className="fullscreen-button"
                        onClick={toggleFullscreen}
                        title="View Fullscreen"
                      >
                        ⛶
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-image-placeholder">
                  {currentProfile?.nickname?.[0] || currentProfile?.email?.[0] || '?'}
                </div>
              )}
            </div>
            
            <div className="content-area">
              <div className="nickname-row">
                <h2>{currentProfile?.nickname || 'No nickname'}</h2>
                {currentProfile?.country && (
                  <img 
                    src={`/flags/${currentProfile.country.toLowerCase()}.webp`} 
                    alt={`${currentProfile.country} flag`} 
                    className="country-flag"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              <p className='join-date'>{currentProfile?.created_at ? new Date(currentProfile.created_at).toLocaleDateString() : 'Join date unknown'}</p>
              <p>
                {currentProfile?.message && currentProfile.message.length > 100 && !isExpanded
                  ? `${currentProfile.message.substring(0, 100)}...`
                  : currentProfile?.message || ''}
                
                {currentProfile?.message && currentProfile.message.length > 100 && (
                  <span className="read-more-text" onClick={toggleExpand}>
                    {isExpanded ? ' Read Less' : ' Read More'}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {isFullscreen && getImageUrl() && (
        <div className="fullscreen-overlay" onClick={toggleFullscreen}>
          <div className="fullscreen-container">
            <img 
              src={getImageUrl()}
              alt={currentProfile.nickname || 'Profile'}
              className="fullscreen-image"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="fullscreen-close" onClick={toggleFullscreen}>×</button>
            <div className="fullscreen-info">
              <p>{currentProfile?.nickname || 'Profile'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShare && freshProfile && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          profile={freshProfile}
          profileUrl={generateProfileUrl()}
        />
      )}
    </>
  );
}

export default ProfilePreviewModal;